import { useEffect, useMemo, useState, type CSSProperties } from "react";
import { useQuery } from "@tanstack/react-query";
import { useActiveAthlete } from "@/hooks/useActiveAthlete";
import { Link } from "wouter";
import AthleteProfile from "@/components/AthleteProfile";
import JournalEntry from "@/components/JournalEntry";
import MediaItem from "@/components/MediaItem";
import Timeline from "@/components/Timeline";
import JournalingAssistant from "@/components/JournalingAssistant";
import FamilyCircleList from "@/components/FamilyCircleList";
import RecruitingTimeline from "@/components/RecruitingTimeline";
import AchievementShowcase from "@/components/AchievementShowcase";
import GettingStartedChecklist from "@/components/GettingStartedChecklist";
import RecruitingNudge from "@/components/RecruitingNudge";
import NewJournalEntryForm from "@/components/NewJournalEntryForm";
import AddMilestoneForm from "@/components/AddMilestoneForm";
import UploadMediaForm from "@/components/UploadMediaForm";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";
import { useAuth } from "@/hooks/useAuth";
import { getCalendarKeyDates, matchSportKey } from "@/lib/recruitingData";
import { Plus, ArrowRight, Upload, Trophy, CheckCircle2, X, BookText, ImageIcon, Flag } from "lucide-react";

type PlanTier = "basic" | "mid" | "premium";

// Shown in the recruiting card when the athlete's sport has no NCAA calendar in the app
function recruitingEmptyMessage(sport?: string): string {
  const s = sport?.trim();
  if (!s) return "Add your athlete's sport to see NCAA recruiting dates.";
  if (/basketball|soccer/i.test(s)) return "Choose men's or women's for your athlete's sport in their profile to see NCAA recruiting dates.";
  return `NCAA recruiting dates for ${s} aren't available yet.`;
}

const TIER_LABEL: Record<PlanTier, string> = {
  basic: "Loop Family",
  mid: "Loop Family",
  premium: "Loop Recruit",
};

const TIER_UNLOCKS: Record<PlanTier, string[]> = {
  basic: [
    "1TB storage for highlights and memories",
    "2 athlete profiles",
    "AI journaling and full sharing in The Loop",
  ],
  mid: [
    "1TB storage for highlights and memories",
    "2 athlete profiles",
    "AI journaling and full sharing in The Loop",
  ],
  premium: [
    "2TB storage for your full legacy archive",
    "Full Recruiting Center access",
    "Priority access to new features",
  ],
};

const DEFAULT_UNLOCKS = [
  "Expanded storage capacity",
  "Premium vault capabilities",
  "Enhanced family collaboration tools",
];

function normalizeTier(raw: string | null): PlanTier | null {
  if (!raw) return null;
  const value = raw.toLowerCase();
  if (value === "basic" || value === "rec") return "basic";
  if (value === "mid" || value === "club") return "mid";
  if (value === "premium" || value === "premium_club") return "premium";
  return null;
}

function clearWelcomeParams() {
  const url = new URL(window.location.href);
  url.searchParams.delete("welcome");
  url.searchParams.delete("tier");
  const nextUrl = `${url.pathname}${url.search}${url.hash}`;
  window.history.replaceState({}, "", nextUrl);
}

const Dashboard = () => {
  useAuth();
  const [journalFormOpen, setJournalFormOpen] = useState(false);
  const [milestoneFormOpen, setMilestoneFormOpen] = useState(false);
  const [uploadFormOpen, setUploadFormOpen] = useState(false);
  const [showWelcomePanel, setShowWelcomePanel] = useState<boolean>(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get("welcome") === "1";
  });
  const [welcomeTier] = useState<PlanTier | null>(() => {
    const params = new URLSearchParams(window.location.search);
    return normalizeTier(params.get("tier"));
  });

  useEffect(() => {
    if (showWelcomePanel) {
      clearWelcomeParams();
    }
  }, [showWelcomePanel]);

  const { athlete } = useActiveAthlete();

  const { data: journalEntries } = useQuery({
    queryKey: ["/api/athletes", athlete?.id, "journal"],
    enabled: !!athlete?.id,
  });

  const { data: mediaItems } = useQuery({
    queryKey: ["/api/athletes", athlete?.id, "media"],
    enabled: !!athlete?.id,
  });

  const { data: milestones } = useQuery({
    queryKey: ["/api/athletes", athlete?.id, "milestones"],
    enabled: !!athlete?.id,
  });

  const { data: familyCircle } = useQuery({
    queryKey: ["/api/athletes", athlete?.id, "family-circle"],
    enabled: !!athlete?.id,
  });

  // Same client-side NCAA calendar the Recruiting page uses (DI, athlete's sport)
  const recruitingSportKey = athlete?.sports ? matchSportKey(athlete.sports) : null;
  const recruitingKeyDates = useMemo(
    () => (recruitingSportKey ? getCalendarKeyDates(recruitingSportKey, "D1", new Date()) : []),
    [recruitingSportKey]
  );

  const recentJournalEntries = journalEntries?.slice(0, 2) || [];
  const recentMediaItems = mediaItems?.slice(0, 4) || [];
  const heroItem = recentMediaItems[0];
  const gridItems = recentMediaItems.slice(1);
  const dashboardTheme: CSSProperties = {
    "--background": "220 58% 9%",
    "--foreground": "0 0% 100%",
    "--card": "221 45% 14%",
    "--card-foreground": "0 0% 100%",
    "--muted": "221 35% 18%",
    "--muted-foreground": "215 20% 80%",
    "--border": "40 70% 55%",
    "--input": "221 25% 20%",
    "--ring": "40 70% 55%",
  };

  const athleteId = athlete?.id;
  const welcomeTitle = welcomeTier ? TIER_LABEL[welcomeTier] : "your upgraded plan";
  const unlockedFeatures = welcomeTier ? TIER_UNLOCKS[welcomeTier] : DEFAULT_UNLOCKS;

  return (
    <div className="min-h-screen bg-transparent text-white" style={dashboardTheme}>
      <div className="container mx-auto px-4 py-6">
        <AthleteProfile />

        {showWelcomePanel && (
          <section className="mb-6">
            <Card className="border-[#E0A458] bg-[#1B263B] shadow-lg overflow-hidden">
              <CardContent className="p-5">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-[#E0A458]">Plan Activated</p>
                    <h2 className="font-montserrat font-bold text-2xl mt-1">Welcome to {welcomeTitle}</h2>
                    <p className="text-slate-300 mt-2">Your subscription is active. Here is what you just unlocked:</p>
                  </div>
                  <Button
                    variant="ghost"
                    className="text-slate-300 hover:text-white hover:bg-white/10 p-2 h-8 w-8"
                    onClick={() => setShowWelcomePanel(false)}
                    aria-label="Dismiss"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>

                <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
                  {unlockedFeatures.map((feature) => (
                    <div key={feature} className="rounded-md border border-[#415A77] bg-[#0D1B2A] p-3 flex items-start gap-2">
                      <CheckCircle2 className="h-4 w-4 mt-0.5 text-[#E0A458]" />
                      <p className="text-sm text-slate-200">{feature}</p>
                    </div>
                  ))}
                </div>

                <div className="mt-5 flex flex-wrap gap-3">
                  <Link href="/media">
                    <Button className="bg-[#E0A458] text-black hover:bg-[#f0b467]">
                      <Upload className="mr-2 h-4 w-4" /> Upload your first highlight
                    </Button>
                  </Link>
                  <Link href="/journal">
                    <Button className="bg-[#E0A458] text-black hover:bg-[#f0b467]">
                      <Plus className="mr-2 h-4 w-4" /> Create first journal entry
                    </Button>
                  </Link>
                  <Link href="/family">
                    <Button className="bg-[#E0A458] text-black hover:bg-[#f0b467]">
                      Invite family to your circle
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          </section>
        )}

        {/* Getting started checklist — shown until dismissed or all 3 steps done */}
        {athleteId && (
          <GettingStartedChecklist
            athleteId={athleteId}
            mediaCount={mediaItems?.length ?? 0}
            journalCount={journalEntries?.length ?? 0}
            familyCount={familyCircle?.length ?? 0}
            onUploadClick={() => setUploadFormOpen(true)}
            onJournalClick={() => setJournalFormOpen(true)}
          />
        )}

        {/* Recent Highlights — full width, directly below profile hero */}
        <section className="mb-6">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h2 className="font-montserrat font-bold text-xl text-white border-l-2 border-[#E0A458] pl-3">Recent Highlights</h2>
              {recentMediaItems.length > 0 && (
                <p className="text-xs text-slate-400 mt-0.5 pl-3">{mediaItems?.length ?? recentMediaItems.length} moment{(mediaItems?.length ?? recentMediaItems.length) !== 1 ? "s" : ""} captured</p>
              )}
            </div>
            {athleteId ? (
              <Dialog open={uploadFormOpen} onOpenChange={setUploadFormOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-[#E0A458] text-black hover:bg-[#f0b467] font-semibold flex items-center text-sm">
                    <Upload className="h-4 w-4 mr-1" /> Upload
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl border-[#E0A458]/40 bg-[#111827] text-slate-100 shadow-2xl max-h-[88vh] overflow-y-auto">
                  <UploadMediaForm athleteId={athleteId} onSuccess={() => setUploadFormOpen(false)} />
                </DialogContent>
              </Dialog>
            ) : (
              <Button
                disabled
                className="bg-gray-600 text-white flex items-center text-sm font-medium cursor-not-allowed"
                title="Create an athlete profile first"
              >
                <Upload className="h-4 w-4 mr-1" /> Upload
              </Button>
            )}
          </div>

          {recentMediaItems.length > 0 ? (
            <div className="space-y-3">
              {/* Hero item — full width, tall */}
              {heroItem && (
                <MediaItem key={heroItem.id} item={heroItem} thumbnailHeight="h-72" />
              )}
              {/* Supporting grid — up to 3 items */}
              {gridItems.length > 0 && (
                <div className={`grid gap-3 ${gridItems.length === 1 ? "grid-cols-1" : gridItems.length === 2 ? "grid-cols-2" : "grid-cols-3"}`}>
                  {gridItems.map((mediaItem) => (
                    <MediaItem key={mediaItem.id} item={mediaItem} thumbnailHeight="h-40" />
                  ))}
                </div>
              )}
              {/* Footer CTA */}
              <div className="pt-1">
                <Link href="/media">
                  <Button variant="outline" className="mx-auto flex items-center py-2 px-8 border-[#A67C52] text-black bg-[#E0A458] hover:bg-[#f0b467]">
                    View full media library
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>
            </div>
          ) : (
            <div className="rounded-xl border border-[#E0A458]/20 bg-[#0D1B2A]/60 flex flex-col items-center gap-3 py-14 text-center">
              <ImageIcon className="h-10 w-10 text-[#E0A458]/50" />
              <p className="text-slate-200 font-semibold">Your story starts here.</p>
              <p className="text-sm text-slate-400 max-w-xs">Every great moment deserves to be saved. Upload your first photo or video and start building the legacy.</p>
              {athleteId && (
                <Button
                  onClick={() => setUploadFormOpen(true)}
                  className="mt-2 bg-[#E0A458] text-black hover:bg-[#f0b467] font-semibold"
                >
                  <Upload className="h-4 w-4 mr-2" /> Upload Your First Moment
                </Button>
              )}
            </div>
          )}
        </section>

        {/* Recruiting nudge — shown once until dismissed */}
        {athleteId && <RecruitingNudge athleteId={athleteId} />}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Main Content */}
          <div className="lg:col-span-2 space-y-6">

            {/* Journal Section */}
            <section>
              <div className="flex justify-between items-center mb-4">
                <h2 className="font-montserrat font-bold text-xl text-white">Journal Entries</h2>
                {athleteId ? (
                  <Dialog open={journalFormOpen} onOpenChange={setJournalFormOpen}>
                    <DialogTrigger asChild>
                      <Button className="bg-primary hover:bg-primary/90 text-white flex items-center text-sm font-medium">
                        <Plus className="h-4 w-4 mr-1" /> New Entry
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl">
                      <NewJournalEntryForm athleteId={athleteId} onSuccess={() => setJournalFormOpen(false)} />
                    </DialogContent>
                  </Dialog>
                ) : (
                  <Button
                    disabled
                    className="bg-gray-400 text-white flex items-center text-sm font-medium cursor-not-allowed"
                    title="Create an athlete profile first"
                  >
                    <Plus className="h-4 w-4 mr-1" /> New Entry
                  </Button>
                )}
              </div>

              <Card className="shadow-md overflow-hidden">
                {!athleteId ? (
                  <CardContent className="p-8 flex flex-col items-center gap-3 text-center">
                    <BookText className="h-8 w-8 text-[#E0A458]/50" />
                    <p className="text-slate-300">Create your athlete profile to start journaling.</p>
                    <p className="text-sm text-slate-400">Your profile is required to organize and track your athletic journey.</p>
                  </CardContent>
                ) : recentJournalEntries.length > 0 ? (
                  recentJournalEntries.map((entry) => <JournalEntry key={entry.id} entry={entry} />)
                ) : (
                  <CardContent className="p-8 flex flex-col items-center gap-3 text-center">
                    <BookText className="h-8 w-8 text-[#E0A458]/50" />
                    <p className="text-slate-300">No journal entries yet.</p>
                    <p className="text-sm text-slate-400">Start documenting your athletic journey.</p>
                  </CardContent>
                )}

                <div className="p-4 bg-[#0D1B2A]/60">
                  <Link href="/journal">
                    <Button variant="outline" className="mx-auto flex items-center py-2 px-8 border-[#A67C52] text-black bg-[#E0A458] hover:bg-[#f0b467]">
                      View all journal entries
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </Link>
                </div>
              </Card>
            </section>

            {/* Legacy Timeline */}
            <section>
              <div className="flex justify-between items-center mb-4">
                <h2 className="font-montserrat font-bold text-xl text-white">Legacy Timeline</h2>
                {athleteId && (
                  <Button
                    onClick={() => setMilestoneFormOpen(true)}
                    className="bg-primary hover:bg-primary/90 text-white flex items-center text-sm font-medium"
                  >
                    <Plus className="h-4 w-4 mr-1" /> Add Milestone
                  </Button>
                )}
              </div>

              <Card className="shadow-md overflow-hidden p-5">
                <Timeline milestones={milestones || []} />

                <Link href="/milestones">
                  <Button variant="outline" className="mx-auto flex items-center mt-6 py-2 px-8 border-[#A67C52] text-black bg-[#E0A458] hover:bg-[#f0b467]">
                    View complete journey
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </Card>

              {/* Add Milestone dialog */}
              <Dialog open={milestoneFormOpen} onOpenChange={setMilestoneFormOpen}>
                <DialogContent className="w-[calc(100vw-2rem)] max-w-xl max-h-[90vh] overflow-y-auto bg-[#0D1B2A] border border-[#E0A458]/30 text-white">
                  <AddMilestoneForm
                    athleteId={athleteId}
                    onSuccess={() => setMilestoneFormOpen(false)}
                  />
                </DialogContent>
              </Dialog>
            </section>
          </div>

          {/* Right Column - Sidebar */}
          <div className="space-y-6">
            {/* AI Journaling Assistant */}
            <JournalingAssistant athleteId={athleteId} />

            {/* In the Loop */}
            <FamilyCircleList familyMembers={familyCircle || []} athleteId={athleteId} />

            {/* Recruiting Timeline */}
            <RecruitingTimeline
              events={recruitingKeyDates}
              athleteSport={recruitingSportKey ?? ""}
              sourceUrl="https://www.ncaa.org/sports/2018/5/8/division-i-and-ii-recruiting-calendars.aspx"
              emptyMessage={recruitingEmptyMessage(athlete?.sports)}
            />


            {/* Achievement Showcase */}
            {athleteId && (
              <section>
                <h3 className="font-montserrat font-bold text-lg text-white mb-3 flex items-center">
                  <Trophy className="mr-2 h-5 w-5 text-primary" />
                  Achievement Showcase
                </h3>
                <AchievementShowcase athleteId={athleteId} />
              </section>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
